import React, { useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MODES = [
  { key: 'classic', label: 'Classic Search' },
  { key: 'firstlast', label: 'First & Last Occurrence' },
  { key: 'rotated', label: 'Rotated Sorted Array' },
  { key: 'sqrt', label: 'Integer Square Root' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

/* ─── Box row with lo/mid/hi pointer tags ───────────────────────── */
const RangeRow = ({ array, lo, mid, hi, dead }) => (
  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 26, paddingBottom: 22 }}>
    {array.map((val, i) => {
      const isDead = dead && dead.includes(i);
      const inRange = lo !== null && hi !== null && i >= lo && i <= hi && !isDead;
      const isMid = i === mid;
      const isLo = i === lo;
      const isHi = i === hi;
      let bg = 'var(--bg3)', border = 'var(--border)', color = 'var(--text)';
      if (isDead) { bg = 'var(--bg2)'; border = 'var(--border)'; color = 'var(--muted)'; }
      else if (isMid) { bg = `linear-gradient(135deg,${V1},${V2})`; border = V1; color = '#fff'; }
      else if (inRange) { bg = 'var(--bg2)'; border = 'var(--border-strong)'; }
      return (
        <div key={i} style={{ position: 'relative' }}>
          {isLo && !isMid && <span style={{ position: 'absolute', top: -22, left: '50%', transform: 'translateX(-50%)', fontSize: 9.5, fontWeight: 800, color: TEAL, whiteSpace: 'nowrap' }}>lo</span>}
          {isHi && !isMid && <span style={{ position: 'absolute', top: -22, left: '50%', transform: 'translateX(-50%)', fontSize: 9.5, fontWeight: 800, color: TEAL, whiteSpace: 'nowrap' }}>hi</span>}
          {isMid && <span style={{ position: 'absolute', top: -22, left: '50%', transform: 'translateX(-50%)', fontSize: 9.5, fontWeight: 800, color: V2, whiteSpace: 'nowrap' }}>mid{isLo ? '=lo' : ''}{isHi ? '=hi' : ''}</span>}
          <div style={{
            width: 46, height: 46, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, fontWeight: 800, fontFamily: "'Inter',sans-serif",
            background: bg, color, border: `1.5px solid ${border}`, transition: 'all 0.2s ease', opacity: isDead ? 0.4 : 1,
          }}>{val}</div>
          <span style={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', bottom: -18, fontSize: 10, fontWeight: 700, color: 'var(--muted)' }}>{i}</span>
        </div>
      );
    })}
  </div>
);

const smallInput = {
  width: 90, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const wideInput = { ...smallInput, width: 220 };
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const ghostBtn = disabled => ({
  padding: '8px 14px', borderRadius: 9, cursor: disabled ? 'default' : 'pointer',
  border: `1.5px solid ${disabled ? 'var(--border)' : V1}`, background: 'transparent',
  color: disabled ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
  fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const StepNav = ({ step, total, onPrev, onNext }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={onPrev} disabled={step === 0} style={ghostBtn(step === 0)}>← Prev</button>
    <button onClick={onNext} disabled={step === total - 1} style={smallBtn(V1, step === total - 1)}>Next →</button>
    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {total}</span>
  </div>
);
const Chip = ({ children, color = V1 }) => (
  <span style={{
    display: 'inline-flex', padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    background: `${color}15`, color, marginRight: 6, marginBottom: 6, fontFamily: 'monospace',
  }}>{children}</span>
);

/* ─── Classic Binary Search ──────────────────────────────────────── */
const ClassicMode = () => {
  const [arrInput, setArrInput] = useState('2, 4, 7, 11, 15, 19, 24, 30');
  const [targetInput, setTargetInput] = useState('19');
  const [arr, setArr] = useState([2, 4, 7, 11, 15, 19, 24, 30]);
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const startSearch = () => {
    const parsed = arrInput.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n)).sort((a, b) => a - b);
    const target = Number(targetInput);
    if (parsed.length === 0 || isNaN(target)) return;
    const built = [];
    let lo = 0, hi = parsed.length - 1;
    while (lo <= hi) {
      const mid = lo + Math.floor((hi - lo) / 2);
      if (parsed[mid] === target) { built.push({ lo, hi, mid, result: 'found' }); break; }
      if (parsed[mid] < target) { built.push({ lo, hi, mid, result: 'toosmall' }); lo = mid + 1; }
      else { built.push({ lo, hi, mid, result: 'toobig' }); hi = mid - 1; }
    }
    if (built.length === 0 || built[built.length - 1].result !== 'found') built.push({ done: true });
    setArr(parsed);
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const note = !cur ? 'Enter a comma-separated list (it will be sorted) and a target, then Search.' :
    cur.done ? 'lo crossed hi with no match — the target isn\'t in the array.' :
    cur.result === 'found' ? `arr[${cur.mid}] = ${arr[cur.mid]} equals the target — found at index ${cur.mid}!` :
    cur.result === 'toosmall' ? `arr[${cur.mid}] = ${arr[cur.mid]} is smaller than the target — search the right half: lo = ${cur.mid + 1}.` :
    `arr[${cur.mid}] = ${arr[cur.mid]} is bigger than the target — search the left half: hi = ${cur.mid - 1}.`;

  const code = {
    lines: [
      'int lo = 0, hi = arr.length - 1;',
      'while (lo <= hi) {',
      '    int mid = lo + (hi - lo) / 2;',
      '    if (arr[mid] == target) return mid;',
      '    else if (arr[mid] < target) lo = mid + 1;',
      '    else hi = mid - 1;',
      '}',
      'return -1;',
    ],
    active: !cur ? -1 : cur.done ? 7 : cur.result === 'found' ? 3 : cur.result === 'toosmall' ? 4 : 5,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <RangeRow array={arr} lo={cur && !cur.done ? cur.lo : null} mid={cur && !cur.done ? cur.mid : null} hi={cur && !cur.done ? cur.hi : null} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Array</span>
            <input value={arrInput} onChange={e => { setArrInput(e.target.value); setSteps(null); }} style={wideInput} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Target</span>
            <input value={targetInput} onChange={e => { setTargetInput(e.target.value); setSteps(null); }} style={smallInput} />
            <button onClick={startSearch} style={smallBtn(V1)}>Search</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{note}</p>
        </div>
      </div>
      <CodePanel title="Java — Classic Binary Search" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── First & Last Occurrence ────────────────────────────────────── */
const FirstLastMode = () => {
  const [arrInput, setArrInput] = useState('2, 4, 4, 4, 7, 9, 9, 11');
  const [targetInput, setTargetInput] = useState('4');
  const [arr, setArr] = useState([2, 4, 4, 4, 7, 9, 9, 11]);
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const startSearch = () => {
    const parsed = arrInput.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n)).sort((a, b) => a - b);
    const target = Number(targetInput);
    if (parsed.length === 0 || isNaN(target)) return;
    const built = [];
    // find first occurrence
    let lo = 0, hi = parsed.length - 1, first = -1;
    while (lo <= hi) {
      const mid = lo + Math.floor((hi - lo) / 2);
      if (parsed[mid] === target) { first = mid; built.push({ lo, hi, mid, phase: 'first', action: 'match' }); hi = mid - 1; }
      else if (parsed[mid] < target) { built.push({ lo, hi, mid, phase: 'first', action: 'toosmall' }); lo = mid + 1; }
      else { built.push({ lo, hi, mid, phase: 'first', action: 'toobig' }); hi = mid - 1; }
    }
    // find last occurrence
    lo = 0; hi = parsed.length - 1; let last = -1;
    while (lo <= hi) {
      const mid = lo + Math.floor((hi - lo) / 2);
      if (parsed[mid] === target) { last = mid; built.push({ lo, hi, mid, phase: 'last', action: 'match' }); lo = mid + 1; }
      else if (parsed[mid] < target) { built.push({ lo, hi, mid, phase: 'last', action: 'toosmall' }); lo = mid + 1; }
      else { built.push({ lo, hi, mid, phase: 'last', action: 'toobig' }); hi = mid - 1; }
    }
    built.push({ done: true, first, last });
    setArr(parsed);
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const note = !cur ? 'Enter a sorted-ish array with duplicates and a target, then Search.' :
    cur.done ? (cur.first === -1 ? 'Target not found.' : `First occurrence at index ${cur.first}, last occurrence at index ${cur.last}.`) :
    cur.action === 'match' ? `arr[${cur.mid}] = ${arr[cur.mid]} matches — ${cur.phase === 'first' ? 'keep searching LEFT for an earlier match (hi = mid − 1)' : 'keep searching RIGHT for a later match (lo = mid + 1)'}, don\'t stop yet.` :
    cur.action === 'toosmall' ? `arr[${cur.mid}] = ${arr[cur.mid]} is too small — lo = ${cur.mid + 1}.` :
    `arr[${cur.mid}] = ${arr[cur.mid]} is too big — hi = ${cur.mid - 1}.`;

  const code = {
    lines: cur && cur.phase === 'last' ? [
      '// Finding the LAST occurrence:',
      'while (lo <= hi) {',
      '    int mid = lo + (hi - lo) / 2;',
      '    if (arr[mid] == target) { last = mid; lo = mid + 1; }',
      '    else if (arr[mid] < target) lo = mid + 1;',
      '    else hi = mid - 1;',
      '}',
    ] : [
      '// Finding the FIRST occurrence:',
      'while (lo <= hi) {',
      '    int mid = lo + (hi - lo) / 2;',
      '    if (arr[mid] == target) { first = mid; hi = mid - 1; }',
      '    else if (arr[mid] < target) lo = mid + 1;',
      '    else hi = mid - 1;',
      '}',
    ],
    active: !cur || cur.done ? -1 : cur.action === 'match' ? 3 : cur.action === 'toosmall' ? 4 : 5,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <RangeRow array={arr} lo={cur && !cur.done ? cur.lo : null} mid={cur && !cur.done ? cur.mid : null} hi={cur && !cur.done ? cur.hi : null} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Array</span>
            <input value={arrInput} onChange={e => { setArrInput(e.target.value); setSteps(null); }} style={wideInput} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Target</span>
            <input value={targetInput} onChange={e => { setTargetInput(e.target.value); setSteps(null); }} style={smallInput} />
            <button onClick={startSearch} style={smallBtn(V1)}>Search</button>
          </div>
          {cur && !cur.done && <Chip color={cur.phase === 'first' ? V1 : TEAL}>{cur.phase === 'first' ? 'Pass 1: find FIRST' : 'Pass 2: find LAST'}</Chip>}
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{note}</p>
        </div>
      </div>
      <CodePanel title="Java — Bias-Left / Bias-Right Binary Search" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Search in Rotated Sorted Array ─────────────────────────────── */
const RotatedMode = () => {
  const [arrInput, setArrInput] = useState('4, 5, 6, 7, 0, 1, 2');
  const [targetInput, setTargetInput] = useState('0');
  const [arr, setArr] = useState([4, 5, 6, 7, 0, 1, 2]);
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const startSearch = () => {
    const parsed = arrInput.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n));
    const target = Number(targetInput);
    if (parsed.length === 0 || isNaN(target)) return;
    const built = [];
    let lo = 0, hi = parsed.length - 1;
    while (lo <= hi) {
      const mid = lo + Math.floor((hi - lo) / 2);
      if (parsed[mid] === target) { built.push({ lo, hi, mid, note: 'found' }); break; }
      const leftSorted = parsed[lo] <= parsed[mid];
      if (leftSorted) {
        if (parsed[lo] <= target && target < parsed[mid]) { built.push({ lo, hi, mid, note: 'left-sorted-in-range' }); hi = mid - 1; }
        else { built.push({ lo, hi, mid, note: 'left-sorted-out-of-range' }); lo = mid + 1; }
      } else {
        if (parsed[mid] < target && target <= parsed[hi]) { built.push({ lo, hi, mid, note: 'right-sorted-in-range' }); lo = mid + 1; }
        else { built.push({ lo, hi, mid, note: 'right-sorted-out-of-range' }); hi = mid - 1; }
      }
    }
    if (built.length === 0 || built[built.length - 1].note !== 'found') built.push({ done: true });
    setArr(parsed);
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const noteText = {
    found: () => `arr[${cur.mid}] equals the target — found at index ${cur.mid}!`,
    'left-sorted-in-range': () => `arr[${cur.lo}..${cur.mid}] is the sorted half, and the target falls inside it — search left: hi = ${cur.mid - 1}.`,
    'left-sorted-out-of-range': () => `arr[${cur.lo}..${cur.mid}] is sorted, but the target isn't in that range — search right: lo = ${cur.mid + 1}.`,
    'right-sorted-in-range': () => `arr[${cur.mid}..${cur.hi}] is the sorted half, and the target falls inside it — search right: lo = ${cur.mid + 1}.`,
    'right-sorted-out-of-range': () => `arr[${cur.mid}..${cur.hi}] is sorted, but the target isn't in that range — search left: hi = ${cur.mid - 1}.`,
  };
  const note = !cur ? 'Enter a rotated sorted array (no duplicates) and a target, then Search.' :
    cur.done ? 'lo crossed hi — the target isn\'t in the array.' : noteText[cur.note]();

  const code = {
    lines: [
      'int mid = lo + (hi - lo) / 2;',
      'if (arr[mid] == target) return mid;',
      'if (arr[lo] <= arr[mid]) {           // left half is sorted',
      '    if (arr[lo] <= target && target < arr[mid]) hi = mid - 1;',
      '    else lo = mid + 1;',
      '} else {                              // right half is sorted',
      '    if (arr[mid] < target && target <= arr[hi]) lo = mid + 1;',
      '    else hi = mid - 1;',
      '}',
    ],
    active: !cur || cur.done ? -1 : cur.note === 'found' ? 1 : cur.note.startsWith('left') ? (cur.note.endsWith('in-range') ? 3 : 4) : (cur.note.endsWith('in-range') ? 6 : 7),
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <RangeRow array={arr} lo={cur && !cur.done ? cur.lo : null} mid={cur && !cur.done ? cur.mid : null} hi={cur && !cur.done ? cur.hi : null} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Rotated array</span>
            <input value={arrInput} onChange={e => { setArrInput(e.target.value); setSteps(null); }} style={wideInput} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Target</span>
            <input value={targetInput} onChange={e => { setTargetInput(e.target.value); setSteps(null); }} style={smallInput} />
            <button onClick={startSearch} style={smallBtn(V1)}>Search</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{note}</p>
        </div>
      </div>
      <CodePanel title="Java — Search in Rotated Sorted Array" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Integer Square Root (Binary Search on the Answer) ──────────── */
const SqrtMode = () => {
  const [nInput, setNInput] = useState('37');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const startSearch = () => {
    const n = parseInt(nInput, 10);
    if (isNaN(n) || n < 0) return;
    const built = [];
    let lo = 0, hi = n, ans = 0;
    while (lo <= hi) {
      const mid = lo + Math.floor((hi - lo) / 2);
      const sq = mid * mid;
      if (sq === n) { ans = mid; built.push({ lo, hi, mid, sq, result: 'exact' }); break; }
      if (sq < n) { ans = mid; built.push({ lo, hi, mid, sq, result: 'small', ans }); lo = mid + 1; }
      else { built.push({ lo, hi, mid, sq, result: 'big' }); hi = mid - 1; }
    }
    built.push({ done: true, ans });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const note = !cur ? 'Enter a non-negative integer n, then Search — this searches over ANSWERS (0..n), not over an array.' :
    cur.done ? `Search space exhausted. Largest mid whose square didn't exceed n: floor(√${nInput}) = ${cur.ans}.` :
    cur.result === 'exact' ? `${cur.mid}² = ${cur.sq} = n exactly — √${nInput} = ${cur.mid}.` :
    cur.result === 'small' ? `${cur.mid}² = ${cur.sq} ≤ n — this could be the answer (or there's a bigger one); remember it and search higher: lo = ${cur.mid + 1}.` :
    `${cur.mid}² = ${cur.sq} > n — too big, search lower: hi = ${cur.mid - 1}.`;

  const code = {
    lines: [
      'int lo = 0, hi = n, ans = 0;',
      'while (lo <= hi) {',
      '    int mid = lo + (hi - lo) / 2;',
      '    long square = (long) mid * mid;',
      '    if (square == n) return mid;',
      '    if (square < n) { ans = mid; lo = mid + 1; }',
      '    else hi = mid - 1;',
      '}',
      'return ans;   // floor(sqrt(n))',
    ],
    active: !cur ? -1 : cur.done ? -1 : cur.result === 'exact' ? 4 : cur.result === 'small' ? 5 : 6,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>n</span>
            <input value={nInput} onChange={e => { setNInput(e.target.value); setSteps(null); }} style={smallInput} />
            <button onClick={startSearch} style={smallBtn(V1)}>Search</button>
          </div>
          {cur && !cur.done && (
            <div>
              <Chip color={TEAL}>lo = {cur.lo}</Chip>
              <Chip color={V1}>mid = {cur.mid}</Chip>
              <Chip color={TEAL}>hi = {cur.hi}</Chip>
              <Chip color={GREEN}>{cur.mid}² = {cur.sq}</Chip>
            </div>
          )}
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{note}</p>
          <p style={{ fontSize: 12.5, color: 'var(--muted)', margin: 0 }}>
            Notice there's no array here at all — lo and hi are candidate ANSWERS, not indices. This is "binary search on the answer": narrow down a range of possible results using a check (mid² vs n) instead of comparing to array elements.
          </p>
        </div>
      </div>
      <CodePanel title="Java — Binary Search on the Answer" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const QUIZ_ARR = [3, 8, 14, 21, 29, 38, 46, 55, 63];
const buildQuizScenario = () => {
  const target = QUIZ_ARR[Math.floor(Math.random() * QUIZ_ARR.length)];
  const lo = Math.floor(Math.random() * 3);
  const hi = QUIZ_ARR.length - 1 - Math.floor(Math.random() * 3);
  const mid = lo + Math.floor((hi - lo) / 2);
  let correct;
  if (QUIZ_ARR[mid] === target) correct = 'found';
  else if (QUIZ_ARR[mid] < target) correct = 'right';
  else correct = 'left';
  return { lo, mid, hi, target, correct };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const pick = choice => {
    if (answer !== null) return;
    setAnswer(choice);
    setScore(s => ({ correct: s.correct + (choice === scenario.correct ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(null); };

  const options = [
    { key: 'left', label: 'arr[mid] is too big — search left (hi = mid − 1)' },
    { key: 'right', label: 'arr[mid] is too small — search right (lo = mid + 1)' },
    { key: 'found', label: 'arr[mid] equals the target — found!' },
  ];

  return (
    <div>
      <RangeRow array={QUIZ_ARR} lo={scenario.lo} mid={scenario.mid} hi={scenario.hi} />
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 14 }}>
        target = {scenario.target}. arr[mid] = {QUIZ_ARR[scenario.mid]}. What should happen next?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {options.map(o => {
          const revealed = answer !== null;
          const isCorrect = o.key === scenario.correct;
          const isPicked = o.key === answer;
          let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
          if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
          else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
          return (
            <button key={o.key} onClick={() => pick(o.key)} disabled={revealed} style={{
              textAlign: 'left', padding: '10px 14px', borderRadius: 10,
              border: `1.5px solid ${border}`, background: bg, color, fontSize: 13.5,
              cursor: revealed ? 'default' : 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}>{o.label}</button>
          );
        })}
      </div>
      {answer !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={next} style={smallBtn(V1)}>Next Question</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</span>
        </div>
      )}
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const BinarySearchPlayground = () => {
  const [mode, setMode] = useState('classic');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'classic' && <ClassicMode />}
      {mode === 'firstlast' && <FirstLastMode />}
      {mode === 'rotated' && <RotatedMode />}
      {mode === 'sqrt' && <SqrtMode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default BinarySearchPlayground;
